/**
 * Screenshots Service — Source of truth for the screenshot feature.
 *
 * State: a single BehaviorSubject<Screenshot[]>. Host components (Takeoff, Bid,
 * Job, …) call setReference({type, id}) once on init so the service knows which
 * record the current view is bound to. From that point on, all CRUD calls are
 * scoped to that primary reference — the backend enforces the same via strict
 * WHERE on reference_type/reference_id (no leaking to sibling records).
 *
 * Backend: /api/v1/screenshots (POST/GET/DELETE/PATCH). Uploads are multipart
 * so the raw image blob replaces the base64 dataURL we used in the mock.
 */

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, delay, map, tap } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { UserService } from 'src/app/services/core/user.service';
import {
  Screenshot,
  ShareRecord,
  Contact,
  ShareFormValue,
  LinkableMeasurement,
} from './screenshots.types';
import { MOCK_CONTACTS } from './screenshots.mock-data';

/** Backend endpoint reference — matches ScreenshotService::MODULE_MAP keys. */
export type ScreenshotReferenceType =
  | 'App\\Models\\Takeoff'
  | 'App\\Models\\Bid'
  | 'App\\Models\\Job'
  | 'App\\Models\\ChangeOrder'
  | 'App\\Models\\Submittal'
  | 'App\\Models\\Ticket'
  | 'App\\Models\\Note';

export interface ScreenshotReference {
  type: ScreenshotReferenceType;
  id: number;
}

@Injectable({ providedIn: 'root' })
export class ScreenshotsService {
  private readonly _screenshots$ = new BehaviorSubject<Screenshot[]>([]);
  readonly screenshots$: Observable<Screenshot[]> = this._screenshots$.asObservable();

  private readonly baseUrl =
    `${environment.apiBaseUrl}${environment.apiUrl}${environment.apiVersion}screenshots`;

  private currentReference: ScreenshotReference | null = null;

  /**
   * Remaps optimistic `pending-…` ids to their real backend ids once the POST
   * completes. The capture-toast host component still holds the pending id in
   * `pendingCapture`, so any late `updateScreenshot('pending-…', …)` needs to
   * be routed to the real row (otherwise the caption typed after upload
   * completes silently disappears).
   */
  private readonly tempIdMap = new Map<string, string>();

  constructor(
    private http: HttpClient,
    private userService: UserService,
  ) {}

  // ============ Context wiring ============

  /**
   * Host component sets its reference once on init. Screenshots taken while
   * this reference is active will be attached to that record on the backend.
   */
  setReference(reference: ScreenshotReference | null): void {
    this.currentReference = reference;
    if (reference) {
      this.load(reference).subscribe();
    } else {
      this._screenshots$.next([]);
    }
  }

  getReference(): ScreenshotReference | null {
    return this.currentReference;
  }

  // ============ CRUD ============

  /**
   * Optimistic add: pushes a temp screenshot into the stream immediately, then
   * POSTs multipart to /api/v1/screenshots. On success, replaces the temp row
   * with the server-returned row (with real id + file_url).
   */
  addScreenshot(partial: Omit<Screenshot, 'id' | 'createdAt' | 'annotations' | 'shares'>): Screenshot {
    const tempId = `pending-${Date.now()}`;
    const optimistic: Screenshot = {
      ...partial,
      id: tempId,
      createdAt: new Date().toISOString(),
      // Stamp the signed-in user so the avatar renders immediately instead of
      // popping in when the POST resolves. The server row overwrites this.
      authorId: this.currentUserId(),
      authorName: this.currentUserName(),
      annotations: [],
      shares: [],
    };
    this._screenshots$.next([optimistic, ...this._screenshots$.value]);

    if (!this.currentReference) {
      // No host reference set — cannot persist. Keep optimistic UI only.
      console.warn('[ScreenshotsService] setReference() was not called; screenshot kept locally only');
      return optimistic;
    }

    const blob = this.dataUrlToBlob(partial.thumbnail);
    const form = new FormData();
    form.append('image', blob, this.buildFileName(partial));
    form.append('reference_type', this.currentReference.type);
    form.append('reference_id', String(this.currentReference.id));
    form.append('title', partial.caption || '');
    if (partial.pageName)                              form.append('page_name', partial.pageName);
    if (Number.isFinite(Number(partial.pageId)))       form.append('page_number', String(partial.pageId));
    if (partial.tabId != null)                         form.append('tab_id', String(partial.tabId));
    if (partial.tabIdType)                             form.append('tab_id_type', partial.tabIdType);
    if (partial.region) {
      // Multipart doesn't carry JSON — send each key so Laravel parses it as
      // an array (matches the `region.*` validation rules on the backend).
      form.append('region[x]',      String(partial.region.x));
      form.append('region[y]',      String(partial.region.y));
      form.append('region[width]',  String(partial.region.width));
      form.append('region[height]', String(partial.region.height));
    }
    if (partial.viewerState) {
      // Persist the PDF viewer's zoom+scroll at capture time via the existing
      // `dimensions` JSON column on screen_shots. Without this, the Locate
      // ring can't tell what zoom the shot was taken at once the row is
      // reloaded from the API, so zoom-aware positioning silently degrades.
      form.append('dimensions[zoomPercentage]', String(partial.viewerState.zoomPercentage));
      form.append('dimensions[scrollLeft]',     String(partial.viewerState.scrollLeft));
      form.append('dimensions[scrollTop]',      String(partial.viewerState.scrollTop));
      // Capture-time PDF page rect (canvasWrapper-local). Locate re-projects
      // onto the CURRENT page element's rect so the ring stays fixed on the
      // same PDF area even after the user zooms.
      if (partial.viewerState.pageRect) {
        form.append('dimensions[pageRect][offsetX]', String(partial.viewerState.pageRect.offsetX));
        form.append('dimensions[pageRect][offsetY]', String(partial.viewerState.pageRect.offsetY));
        form.append('dimensions[pageRect][width]',   String(partial.viewerState.pageRect.width));
        form.append('dimensions[pageRect][height]',  String(partial.viewerState.pageRect.height));
      }
      if (partial.viewerState.pageIndex != null) {
        form.append('dimensions[pageIndex]', String(partial.viewerState.pageIndex));
      }
    }

    // API envelope: { data: { success, message, data: <ScreenshotResource> } }
    this.http.post<{ data: { data: any } }>(this.baseUrl, form).subscribe({
      next: (res) => this.replaceTemp(tempId, this.toScreenshot(res?.data?.data, partial.thumbnail)),
      error: (err) => {
        console.error('[ScreenshotsService] upload failed', err);
        // leave the optimistic row so the user can retry; up to caller to surface a toast
      },
    });

    return optimistic;
  }

  updateScreenshot(id: string, patch: Partial<Screenshot>): void {
    // If the caller still holds a temp id whose row was already swapped in by
    // replaceTemp, remap to the real id so the mutation lands on the right row
    // and can actually be PATCHed to the backend.
    const resolvedId = this.tempIdMap.get(id) ?? id;

    const next = this._screenshots$.value.map((s) =>
      s.id === resolvedId ? { ...s, ...patch } : s
    );
    this._screenshots$.next(next);

    if (resolvedId.startsWith('pending-')) return; // not persisted yet

    const body: any = {};
    if (patch.caption !== undefined)  body.title       = patch.caption;
    if (patch.pageName !== undefined) body.page_name   = patch.pageName;
    if (patch.region !== undefined)   body.region      = patch.region;
    if (Object.keys(body).length === 0) return;

    this.http.patch(`${this.baseUrl}/${resolvedId}`, body).subscribe({
      error: (err) => console.error('[ScreenshotsService] update failed', err),
    });
  }

  deleteScreenshot(id: string): void {
    this._screenshots$.next(this._screenshots$.value.filter((s) => s.id !== id));
    if (!id || id === 'undefined' || id.startsWith('pending-')) return;
    this.http.delete(`${this.baseUrl}/${id}`).subscribe({
      error: (err) => console.error('[ScreenshotsService] delete failed', err),
    });
  }

  deleteScreenshots(ids: string[]): void {
    const idSet = new Set(ids);
    this._screenshots$.next(this._screenshots$.value.filter((s) => !idSet.has(s.id)));
    const persistedIds = ids
      .filter((id) => !id.startsWith('pending-'))
      .map((id) => Number(id))
      .filter((n) => Number.isFinite(n));
    if (persistedIds.length === 0) return;
    this.http.request('delete', this.baseUrl, { body: { ids: persistedIds } }).subscribe({
      error: (err) => console.error('[ScreenshotsService] bulk delete failed', err),
    });
  }

  linkMeasurement(id: string, measurement: LinkableMeasurement | null): void {
    this.updateScreenshot(id, {
      linkedMeasurementId: measurement?.id,
      linkedMeasurementName: measurement?.name,
      linkedMeasurementColor: measurement?.color,
    });
  }

  addShareRecord(id: string, record: ShareRecord): void {
    const current = this._screenshots$.value.find((s) => s.id === id);
    if (!current) return;
    const shares = [...(current.shares ?? []), record];
    this.updateScreenshot(id, { shares });
  }

  // ============ Load ============

  /** GET /api/v1/screenshots?reference_type=…&reference_id=… */
  load(reference: ScreenshotReference): Observable<Screenshot[]> {
    const url = `${this.baseUrl}?reference_type=${encodeURIComponent(reference.type)}&reference_id=${reference.id}&per_page=200`;
    // API envelope: { data: { success, message, data: [ …rows ], meta: {…} } }
    return this.http.get<{ data: { data: any[] } }>(url).pipe(
      map((res) => (res?.data?.data ?? []).map((r) => this.toScreenshot(r))),
      tap((list) => this._screenshots$.next(list)),
      catchError((err) => {
        console.error('[ScreenshotsService] load failed', err);
        this._screenshots$.next([]);
        return of([]);
      })
    );
  }

  // ============ Typeahead (unchanged — still mock until contacts API exists) ============

  searchContacts(query: string): Observable<Contact[]> {
    const q = (query ?? '').trim().toLowerCase();
    if (!q) return of(MOCK_CONTACTS.slice(0, 8)).pipe(delay(80));
    const matches = MOCK_CONTACTS.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q) ||
        (c.company ?? '').toLowerCase().includes(q)
    );
    return of(matches).pipe(delay(120));
  }

  /** Share flow is not backed by the /screenshots API yet — keep mock. */
  shareData(
    _takeoffId: number | string | null,
    screenshotIds: string[],
    form: ShareFormValue
  ): Observable<ShareRecord> {
    const record: ShareRecord = {
      id: `sh-${Date.now()}`,
      channel: form.channel,
      recipients: form.recipients,
      access: form.access,
      expiration: form.expiration,
      allowDownload: form.allowDownload,
      allowComments: form.allowComments,
      requireResponse: form.requireResponse,
      includeAnnotations: form.includeAnnotations,
      includeLinkedMeasurement: form.includeLinkedMeasurement,
      shareUrl: `https://takeoff.app/s/${screenshotIds[0]}-${Date.now()}`,
      password: form.access === 'password' ? form.password : undefined,
      viewCount: 0,
      status: 'sent',
      message: form.message,
      createdAt: new Date().toISOString(),
    };
    return of(record).pipe(delay(140));
  }

  /** Kept for callers that used the old crash-recovery hook. */
  restoreLocal(): Screenshot[] {
    return [];
  }

  // ============ Helpers ============

  private replaceTemp(tempId: string, real: Screenshot): void {
    // Snapshot the temp row BEFORE the swap so we can inspect what the user
    // typed while the upload was in flight.
    const temp = this._screenshots$.value.find((s) => s.id === tempId);
    const preservedCaption = temp?.caption && !real.caption ? temp.caption : real.caption;

    // Record the id remap so any callers still holding the pending id (the
    // capture-toast host, mostly) get routed to the real row + real PATCH.
    this.tempIdMap.set(tempId, String(real.id));

    this._screenshots$.next(
      this._screenshots$.value.map((s) => {
        if (s.id !== tempId) return s;
        // Preserve client-only viewerState set at capture time so "Locate"
        // can still restore scroll/zoom after the server response arrives.
        // Also preserve the caption the user typed in the toast — the initial
        // POST fires with an empty title because the toast is only shown after
        // upload starts, so the caption needs to hitch a ride here.
        return {
          ...real,
          viewerState: s.viewerState ?? real.viewerState,
          caption: preservedCaption,
        };
      })
    );

    // If we kept a locally-typed caption, push it to the backend now that we
    // have a real id. updateScreenshot no-ops on the local mutation because the
    // list already reflects it; we just want the PATCH.
    if (temp?.caption && temp.caption !== real.caption) {
      this.updateScreenshot(String(real.id), { caption: temp.caption });
    }
  }

  /**
   * Display name for the capturing user. Prefers the API's `full_name`, then
   * first+last, so a row with only partial name data still yields initials.
   * Returns undefined when there's nothing usable — the card hides the avatar
   * rather than rendering an empty circle.
   */
  private buildAuthorName(creator: any): string | undefined {
    if (!creator) return undefined;
    const full = (creator.full_name ?? '').toString().trim();
    if (full) return full;
    const composed = [creator.first_name, creator.last_name]
      .map((part: any) => (part ?? '').toString().trim())
      .filter(Boolean)
      .join(' ');
    return composed || undefined;
  }

  /** Signed-in user's id, for stamping optimistic rows. */
  private currentUserId(): number | undefined {
    const id = Number(this.userService.getID());
    return Number.isFinite(id) && id > 0 ? id : undefined;
  }

  /** Signed-in user's display name, for stamping optimistic rows. */
  private currentUserName(): string | undefined {
    return this.buildAuthorName({
      first_name: this.userService.getFirstName(),
      last_name: this.userService.getLastName(),
    });
  }

  private toScreenshot(row: any, fallbackThumbnail: string = ''): Screenshot {
    return {
      id: String(row.id),
      pageId: row.page_number != null ? String(row.page_number) : '1',
      pageName: row.page_name ?? '',
      tabId: row.tab_id != null ? Number(row.tab_id) : undefined,
      tabIdType: row.tab_id_type ?? undefined,
      region: this.coerceRegion(row.region),
      thumbnail: row.file_url ?? fallbackThumbnail,
      caption: row.title ?? '',
      createdAt: row.created_at ?? new Date().toISOString(),
      authorId: row.creator?.id != null ? Number(row.creator.id) : undefined,
      authorName: this.buildAuthorName(row.creator),
      annotations: [],
      shares: [],
      // Rehydrate the viewer state captured at upload time so hosts (e.g.
      // takeoffs) can position the Locate ring correctly regardless of the
      // user's current zoom/scroll. Stored piggyback on the `dimensions`
      // JSON column; missing on legacy rows uploaded before this was added.
      viewerState: this.coerceViewerState(row.dimensions),
    };
  }

  /** Parse the `dimensions` JSON blob back into a CaptureViewerState (or undefined). */
  private coerceViewerState(dimensions: any): {
    scrollLeft: number;
    scrollTop: number;
    zoomPercentage: number;
    pageRect?: { offsetX: number; offsetY: number; width: number; height: number };
    pageIndex?: number;
  } | undefined {
    if (!dimensions || typeof dimensions !== 'object') return undefined;
    const zoomPercentage = Number(dimensions.zoomPercentage);
    if (!Number.isFinite(zoomPercentage) || zoomPercentage <= 0) return undefined;

    const rawRect = dimensions.pageRect;
    const pageRect = rawRect && typeof rawRect === 'object' ? {
      offsetX: Number(rawRect.offsetX) || 0,
      offsetY: Number(rawRect.offsetY) || 0,
      width:   Number(rawRect.width)   || 0,
      height:  Number(rawRect.height)  || 0,
    } : undefined;
    const pageIndex = Number.isFinite(Number(dimensions.pageIndex))
      ? Number(dimensions.pageIndex)
      : undefined;

    return {
      zoomPercentage,
      scrollLeft: Number(dimensions.scrollLeft) || 0,
      scrollTop:  Number(dimensions.scrollTop)  || 0,
      pageRect,
      pageIndex,
    };
  }

  /** Multipart-legacy rows may store region values as strings — coerce here. */
  private coerceRegion(region: any): { x: number; y: number; width: number; height: number } {
    if (!region || typeof region !== 'object') return { x: 0, y: 0, width: 0, height: 0 };
    return {
      x:      Number(region.x)      || 0,
      y:      Number(region.y)      || 0,
      width:  Number(region.width)  || 0,
      height: Number(region.height) || 0,
    };
  }

  private dataUrlToBlob(dataUrl: string): Blob {
    const [meta, base64] = dataUrl.split(',');
    const mimeMatch = /data:(.*?);base64/.exec(meta);
    const mime = mimeMatch ? mimeMatch[1] : 'image/png';
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mime });
  }

  private buildFileName(partial: Omit<Screenshot, 'id' | 'createdAt' | 'annotations' | 'shares'>): string {
    const base = (partial.caption || 'screenshot')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-_]/gi, '')
      .toLowerCase() || 'screenshot';
    return `${base}-${Date.now()}.png`;
  }
}
